import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDocs, limit, query as buildQuery, startAfter } from 'firebase/firestore';
import { PAGING } from '../config/paging';

/**
 * Generic Firestore pagination helper using getDocs + limit + startAfter.
 *
 * Usage:
 *   const baseQuery = useMemo(
 *     () => query(collection(db, 'submissions'), where('isPublic', '==', true), orderBy('createdAt', 'desc')),
 *     []
 *   );
 *
 *   const {
 *     docs,
 *     loading,
 *     loadingMore,
 *     error,
 *     hasMore,
 *     loadMore,
 *     refresh,
 *     lastDoc
 *   } = usePaginatedQuery({ baseQuery, pageSize: PAGING.DEFAULT_PAGE_SIZE });
 */
export function usePaginatedQuery({ baseQuery, pageSize = PAGING.DEFAULT_PAGE_SIZE }) {
  const [docs, setDocs] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Guard against stale async responses
  const requestIdRef = useRef(0);

  const resetState = useCallback(() => {
    setDocs([]);
    setLastDoc(null);
    setHasMore(true);
    setError(null);
  }, []);

  const fetchPage = useCallback(
    async ({ initial }) => {
      if (!baseQuery) return;

      const currentRequestId = ++requestIdRef.current;

      if (initial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        let q = baseQuery;
        if (initial || !lastDoc) {
          q = buildQuery(baseQuery, limit(pageSize));
        } else {
          q = buildQuery(baseQuery, startAfter(lastDoc), limit(pageSize));
        }

        const snap = await getDocs(q);
        // Ignore if a newer request has started since this one
        if (currentRequestId !== requestIdRef.current) return;

        const newDocs = snap.docs;
        setLastDoc(newDocs.length > 0 ? newDocs[newDocs.length - 1] : lastDoc);

        setDocs(prev => (initial ? newDocs : [...prev, ...newDocs]));
        setHasMore(newDocs.length === pageSize);
        setError(null);
      } catch (err) {
        console.error('[usePaginatedQuery] Error fetching page:', err);
        setError(err);
      } finally {
        if (initial) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [baseQuery, lastDoc, pageSize]
  );

  // Initial load + reload when baseQuery changes
  useEffect(() => {
    if (!baseQuery) return;
    resetState();
    fetchPage({ initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseQuery]);

  const loadMore = useCallback(() => {
    if (!baseQuery || loading || loadingMore || !hasMore) return;
    return fetchPage({ initial: false });
  }, [baseQuery, fetchPage, hasMore, loading, loadingMore]);

  const refresh = useCallback(() => {
    if (!baseQuery) return;
    resetState();
    return fetchPage({ initial: true });
  }, [baseQuery, fetchPage, resetState]);

  return useMemo(
    () => ({
      docs,
      lastDoc,
      loading,
      loadingMore,
      error,
      hasMore,
      loadMore,
      refresh,
    }),
    [docs, lastDoc, loading, loadingMore, error, hasMore, loadMore, refresh]
  );
}

